import React from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { devSurfacesEnabled, handleRedirectCallback, restoreSession } from "./authProvider"; // jve-2-4: PKCE round-trip + silent session restore
import { ErrorBoundary } from "./observability/ErrorBoundary"; // jve-4-4: React render-error capture
import { installProblemTracking } from "./observability/install"; // jve-4-4: global error/rejection + web-vitals
import "./generated/tokens.css"; // jve-3: --fawkes-* tokens (default docasoft-green on :root)
import "./generated/themes/warm.css"; // jve-3-1b: theme matrix — activated via data-fawkes-theme
import "./generated/themes/dark.css";
import "./generated/themes/high-contrast.css";
import "@fawkes/ui/styles.css"; // jve-3-3: the @fawkes/ui component styles (consume the tokens)
import "./styles.css";
import "./mobile.css"; // de mobiele laag: alleen @media-regels, laadt als laatste
import { applyStoredTheme } from "./theme";

// Voorkeuren → Thema: apply the user's persisted theme choice before first paint so it survives a refresh.
applyStoredTheme();

const root = ReactDOM.createRoot(document.getElementById("root")!);

// jve-4-4: start problem-tracking before the app renders so an error during the
// first paint is still captured and sent to the backend sink (Story jve-4-3).
//
// jve-2-4: if the page loaded at the OIDC redirect URI with a `?code`, finish the PKCE exchange
// (token → memory) before first render so the initial data calls carry the bearer. Without a
// configured issuer this resolves immediately to false (jve-2-2 runtime is gated on a live issuer).
// spa-session: finish any OIDC redirect first, then — if no token landed in memory — try a silent
// restore from the HttpOnly refresh cookie so a refresh / new tab stays logged in. Both resolve
// immediately to false when not applicable (no `?code`, no cookie), so the login page still shows.
function bootApp(): void {
  installProblemTracking();
  handleRedirectCallback()
    .then((handled) => (handled ? true : restoreSession()))
    .catch(() => undefined)
    .finally(() => {
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </React.StrictMode>,
      );
    });
}

// Dev-only design playground: no-login surfaces at /gallery (the @fawkes/ui component stories) and
// /tiles (the Dier-detail tile fixtures), so the team can iterate on component design without booting
// the authed app. Unlike DEV-only dead code, these are runtime-gated (devSurfacesEnabled): enabled
// under `vite dev` and on a deployed TEST instance, hidden on acc/production. The single promoted
// artifact is identical across environments, so the gate cannot be build-time — the chunks ship as
// lazy imports that acc/production simply never fetch (a manual /gallery hit there boots the app).
const galleryPath = (globalThis.location?.pathname ?? "/").replace(/^\/new(?=\/|$)/, "");
const wantsGallery = galleryPath.startsWith("/gallery") || galleryPath.startsWith("/tiles");

if (wantsGallery) {
  void devSurfacesEnabled().then((enabled) => {
    if (!enabled) {
      bootApp();
      return;
    }
    const loadGallery = galleryPath.startsWith("/tiles")
      ? import("./gallery/TileGallery").then(({ TileGallery }) => <TileGallery />)
      : import("./gallery/ComponentGallery").then(({ ComponentGallery }) => <ComponentGallery />);
    void loadGallery.then((surface) => {
      root.render(
        <React.StrictMode>
          <ErrorBoundary>{surface}</ErrorBoundary>
        </React.StrictMode>,
      );
    });
  });
} else {
  bootApp();
}
